// Was ein Beleg kostet — die EINZIGE Stelle, an der das gerechnet wird.
//
// Editor, Vorschau, Server und PDF importieren alle hier. Das ist Absicht: In
// PITLANE existiert dieselbe Summenlogik dreimal (Editor, Vorschau, PDF) und
// muss von Hand synchron gehalten werden — die Kommentare dort warnen selbst
// davor. Sobald eine Kopie abweicht, zeigt das PDF etwas anderes als der
// Bildschirm, und der Kunde bekommt eine falsche Rechnung.
//
// ══ Zwei Festlegungen, die man kennen muss ══
//
// 1. PREISE SIND BRUTTO. 0,32 € pro Ei ist der Ladenpreis, nicht der Nettopreis.
//    Die Steuer wird herausgerechnet, nicht aufgeschlagen. Genau so macht es
//    api/_lib/handlers/stop-abschliessen.ts für die Tour-Rechnungen — würde der Handbeleg
//    anders rechnen, ergäben zwei Rechnungen über dieselbe Ware zwei Beträge.
//
// 2. GERECHNET WIRD IN GANZEN CENT. Keine Fließkommazahlen: 0.1 + 0.2 ist nicht
//    0.3, und bei Geld darf das nicht passieren.

export interface BelegPosition {
  /** Produkt aus dem Katalog — null bei Freitext-Position */
  product_id: string | null;
  description: string;
  qty: number;
  unit: string;
  /** BRUTTO-Einzelpreis in Cent */
  unit_price_cents: number;
  vat_rate: number;
}

export interface Summen {
  /** Netto ohne Versand */
  nettoCents: number;
  versandCents: number;
  /** Netto inkl. Versand — die Bemessungsgrundlage der Steuer */
  nettoMitVersandCents: number;
  mwstCents: number;
  /** Was der Kunde zahlt */
  bruttoCents: number;
  /** Aufschlüsselung je Steuersatz — gehört auf jede Rechnung */
  nachSatz: { satz: number; nettoCents: number; mwstCents: number; bruttoCents: number }[];
}

/** Zeilensumme (brutto). Die Menge darf gebrochen sein (2,5 kg), der Betrag nie. */
export function zeilenSumme(p: Pick<BelegPosition, "qty" | "unit_price_cents">): number {
  return Math.round(p.qty * p.unit_price_cents);
}

/**
 * Welcher Steuersatz gilt für den Versand?
 *
 * Versandkosten sind steuerlich eine Nebenleistung — sie teilen den Satz der
 * Hauptleistung. Bei Hof Sirges ist alles 7 % (Lebensmittel), das ist der
 * Normalfall. Sind die Positionen gemischt, wäre streng genommen anteilig
 * aufzuteilen; hier gilt der höchste vorkommende Satz. Das ist die konservative
 * Variante: lieber zu viel Steuer ausweisen als zu wenig.
 */
function versandSatz(positionen: BelegPosition[]): number {
  const saetze = positionen.map((p) => p.vat_rate).filter((r) => Number.isFinite(r));
  return saetze.length ? Math.max(...saetze) : 7;
}

export function berechneSummen(positionen: BelegPosition[], versandCents = 0): Summen {
  const versand = Math.max(0, Math.round(versandCents));

  // Brutto je Steuersatz sammeln — der Bruttobetrag ist die Wahrheit.
  const bruttoProSatz = new Map<number, number>();
  for (const p of positionen) {
    bruttoProSatz.set(p.vat_rate, (bruttoProSatz.get(p.vat_rate) ?? 0) + zeilenSumme(p));
  }
  if (versand > 0) {
    const s = versandSatz(positionen);
    bruttoProSatz.set(s, (bruttoProSatz.get(s) ?? 0) + versand);
  }

  // Netto EINMAL je Steuersatz herausrechnen, nicht je Zeile — sonst summieren
  // sich die Rundungsreste und netto + Steuer trifft die Bruttosumme nicht mehr.
  // Die Steuer ist die Differenz, damit die Summe per Konstruktion aufgeht.
  const nachSatz = [...bruttoProSatz.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([satz, brutto]) => {
      const netto = Math.round(brutto / (1 + satz / 100));
      return { satz, nettoCents: netto, mwstCents: brutto - netto, bruttoCents: brutto };
    });

  const bruttoCents = nachSatz.reduce((s, x) => s + x.bruttoCents, 0);
  const mwstCents = nachSatz.reduce((s, x) => s + x.mwstCents, 0);
  const nettoMitVersandCents = bruttoCents - mwstCents;

  // Netto des Versands separat, damit die Rechnung ihn ausweisen kann.
  const versandNetto = versand > 0 ? Math.round(versand / (1 + versandSatz(positionen) / 100)) : 0;

  return {
    nettoCents: nettoMitVersandCents - versandNetto,
    versandCents: versand,
    nettoMitVersandCents,
    mwstCents,
    bruttoCents,
    nachSatz,
  };
}

export const euroCent = (cents: number) =>
  (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
